import { axiosBaseQuery } from '@client';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import {
  DaHelperAddPagesForm,
  DaHelperFeedbackForm,
  DaSummaryRegistrationRequest,
  DaSummaryCachedResponse
} from './models';
import { HlAiExpensesInAttachments } from '@state';

export type TriggerLocateExpensesTrigger = ReturnType<
  typeof useTriggerLocateExpensesMutation
>[0];

export type PollLocateExpensesTrigger = ReturnType<
  typeof usePollLocateExpensesMutation
>[0];

export type GetCachedDaHelperResponseTrigger = ReturnType<
  typeof useLazyGetCachedDaHelperResponseQuery
>[0];

export type DaHelperLocateStartedResponse = {
  uuid: string;
};

export type DaHelperLocateProcessingResponse = {
  message: string;
};

export type DaHelperLocateAlreadyCachedResponse = {
  cached: true;
};

export type DaHelperLocatePollResponse = {
  status: 'PROCESSING' | 'COMPLETED';
};

export const expenseEvaluationDaHelperApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'hl-ai-da-helper-api',
  tagTypes: ['EVALUATION_CACHED_AI_SUMMARY'],
  endpoints: (build) => ({
    rateDaHelperAttachmentResponse: build.mutation<void, DaHelperFeedbackForm>({
      query: (data) => ({
        url: `/secured/da-helper/feedback`,
        method: 'POST',
        data
      })
    }),
    removeDaHelperAttachmentPage: build.mutation<
      void,
      { historyRecordId: number; pageNumber: number }
    >({
      query: ({ historyRecordId, pageNumber }) => ({
        url: `/secured/da-helper/history-record-id/${historyRecordId}/page/${pageNumber}/delete`,
        method: 'DELETE'
      })
    }),
    addDaHelperAttachmentPages: build.mutation<
      void,
      { historyRecordId: number; data: DaHelperAddPagesForm }
    >({
      query: ({ historyRecordId, data }) => ({
        url: `/secured/da-helper/history-record-id/${historyRecordId}/add-pages`,
        method: 'POST',
        data
      })
    }),
    resetToOriginalDaHelperPages: build.mutation<
      void,
      { historyRecordId: number }
    >({
      query: ({ historyRecordId }) => ({
        url: `/secured/da-helper/history-record-id/${historyRecordId}/reset-original-pages`,
        method: 'PATCH'
      })
    }),
    getCachedDaHelperResponse: build.query<
      HlAiExpensesInAttachments,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/da-helper/evaluation/${evaluationId}/get-cache`,
        method: 'GET'
      })
    }),
    triggerLocateExpenses: build.mutation<
      | DaHelperLocateAlreadyCachedResponse
      | DaHelperLocateStartedResponse
      | DaHelperLocateProcessingResponse,
      { evaluationId: number; reset?: boolean }
    >({
      query: ({ evaluationId, reset = false }) => ({
        url: `/secured/da-helper/evaluation/${evaluationId}/locate-expenses?reset=${reset}`,
        method: 'POST'
      })
    }),
    pollLocateExpenses: build.mutation<
      DaHelperLocatePollResponse,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/da-helper/evaluation/${evaluationId}/locate-expenses/poll`,
        method: 'POST'
      })
    }),
    getCachedDaSummaryAiResponse: build.query<
      DaSummaryCachedResponse | null,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/da-summary/evaluation/${evaluationId}/cached-ai-response`,
        method: 'GET'
      }),
      providesTags: (_res, _error, { evaluationId }) =>
        evaluationId
          ? [{ type: 'EVALUATION_CACHED_AI_SUMMARY', id: evaluationId }]
          : ['EVALUATION_CACHED_AI_SUMMARY']
    }),
    registerDaSummaryAiResponse: build.mutation<
      void,
      DaSummaryRegistrationRequest
    >({
      query: (data) => ({
        url: `/secured/da-summary/register-ai-response`,
        method: 'POST',
        data
      }),
      invalidatesTags: (_res, _error, data) =>
        data?.evaluationId
          ? [{ type: 'EVALUATION_CACHED_AI_SUMMARY', id: data.evaluationId }]
          : ['EVALUATION_CACHED_AI_SUMMARY']
    }),
    triggerOcrAndLocate: build.mutation<void, { proformaId: number }>({
      query: ({ proformaId }) => ({
        url: `/secured/da-helper/proforma/${proformaId}/ocr-and-locate-trigger`,
        method: 'POST'
      })
    })
  })
});

export const {
  useAddDaHelperAttachmentPagesMutation,
  useLazyGetCachedDaHelperResponseQuery,
  useGetCachedDaSummaryAiResponseQuery,
  useRateDaHelperAttachmentResponseMutation,
  useRegisterDaSummaryAiResponseMutation,
  useRemoveDaHelperAttachmentPageMutation,
  useResetToOriginalDaHelperPagesMutation,
  useTriggerLocateExpensesMutation,
  usePollLocateExpensesMutation,
  useTriggerOcrAndLocateMutation
} = expenseEvaluationDaHelperApi;
